import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, RefreshCw, MessageCircle, Phone, Mail, ArrowLeft, ExternalLink, Star } from 'lucide-react';
import Header from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { authService } from '../services/authService';
import { operacoesService } from '../services/operacoesService';
import socketService from '../services/socketService';
import { toast } from '../hooks/use-toast';
import { getFileUrl, getInitials } from '../utils/dashboardHelpers';
import { PainelOperacionalResponse, PainelTicket, AtendenteCarga, CanalTicket, PeriodoPainel } from '../types/operacoes';

type Urgencia = 'ok' | 'warn' | 'crit';
type Bucket = 'pendente' | 'atendimento';

const THRESH_PENDENTE = { warn: 10 * 60 * 1000, crit: 20 * 60 * 1000 };
const THRESH_ATENDIMENTO = { warn: 15 * 60 * 1000, crit: 30 * 60 * 1000 };

const CANAL_LABEL: Record<CanalTicket, string> = {
  EVOLUTION: 'WhatsApp',
  UAZAPI: 'WhatsApp',
  META: 'Meta',
  DESCONHECIDO: 'Canal'
};

function urgencia(ms: number, thresh: { warn: number; crit: number }): Urgencia {
  if (ms >= thresh.crit) return 'crit';
  if (ms >= thresh.warn) return 'warn';
  return 'ok';
}

function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(sec)}`;
  if (h > 0) return `${h}:${pad(m)}:${pad(sec)}`;
  return `${m}:${pad(sec)}`;
}

function fmtDuracaoSegundos(seg: number | null): string {
  if (seg === null || seg === undefined) return '—';
  return fmtElapsed(seg * 1000);
}

function fmtAvaliacao(nota: number | null): string {
  if (nota === null || nota === undefined) return '—';
  return nota.toFixed(1);
}

const PERIODO_LABEL: Record<PeriodoPainel, string> = {
  hoje: 'Hoje',
  '7dias': '7 dias',
  '30dias': '30 dias',
  '90dias': '90 dias'
};

const URGENCY_CLASSES: Record<Urgencia, string> = {
  ok: 'border-l-emerald-500 text-emerald-600 dark:text-emerald-400',
  warn: 'border-l-amber-500 text-amber-600 dark:text-amber-400',
  crit: 'border-l-red-500 text-red-600 dark:text-red-400'
};

const STATUS_ATENDENTE_LABEL: Record<string, string> = {
  online: 'Online',
  offline: 'Offline',
  ausente: 'Ausente'
};

const STATUS_ATENDENTE_DOT: Record<string, string> = {
  online: 'bg-emerald-500',
  offline: 'bg-zinc-400 dark:bg-zinc-600',
  ausente: 'bg-amber-500'
};

const PainelOperacoesPage: React.FC = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const isGestor = user?.role === 'Admin' || user?.role === 'SuperAdmin';

  const [data, setData] = useState<PainelOperacionalResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filtroAtendenteId, setFiltroAtendenteId] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState<PeriodoPainel>('hoje');
  const [, setTick] = useState(0);

  const [ticketSelecionado, setTicketSelecionado] = useState<PainelTicket | null>(null);
  const [bucketSelecionado, setBucketSelecionado] = useState<Bucket | null>(null);

  useEffect(() => {
    if (user && !isGestor) {
      navigate('/dashboard');
    }
  }, [user, isGestor, navigate]);

  const fetchPainel = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    else setRefreshing(true);
    try {
      const resposta = await operacoesService.getPainel(periodo);
      setData(resposta);
    } catch (error) {
      console.error('Erro ao carregar painel operacional:', error);
      if (!silencioso) {
        toast({ title: 'Não foi possível carregar o painel.', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [periodo]);

  useEffect(() => {
    if (isGestor) fetchPainel();
  }, [isGestor, fetchPainel]);

  // Cronômetros ao vivo
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Atualização quase em tempo real via socket (reaproveita a conexão já usada no Dashboard)
  const fetchPainelRef = useRef(fetchPainel);
  fetchPainelRef.current = fetchPainel;

  useEffect(() => {
    if (!isGestor) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    let cancelado = false;
    const handleAtualizacao = () => fetchPainelRef.current(true);

    socketService.connect(token).then(() => {
      if (cancelado) return;
      socketService.on('chatListUpdated', handleAtualizacao);
      socketService.on('conversa_atualizada', handleAtualizacao);
      socketService.on('conversa_removida', handleAtualizacao);
    }).catch((err) => console.error('Erro ao conectar socket no painel operacional:', err));

    return () => {
      cancelado = true;
      socketService.off('chatListUpdated', handleAtualizacao);
      socketService.off('conversa_atualizada', handleAtualizacao);
      socketService.off('conversa_removida', handleAtualizacao);
    };
  }, [isGestor]);

  const filtrar = useCallback((tickets: PainelTicket[]) => {
    let lista = tickets;
    if (filtroAtendenteId) {
      lista = lista.filter((t) => t.atendente?.id === filtroAtendenteId);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      lista = lista.filter((t) =>
        (t.contato?.nome || '').toLowerCase().includes(q) ||
        (t.contato?.telefone || '').toLowerCase().includes(q) ||
        (t.assunto || '').toLowerCase().includes(q) ||
        t.protocolo.toLowerCase().includes(q)
      );
    }
    return lista;
  }, [filtroAtendenteId, search]);

  const pendentes = useMemo(() => filtrar(data?.pendentes || []), [data, filtrar]);
  const atendimento = useMemo(() => filtrar(data?.atendimento || []), [data, filtrar]);

  const esperaMediaMs = useMemo(() => {
    const lista = data?.pendentes || [];
    if (!lista.length) return null;
    const soma = lista.reduce((acc, t) => acc + (Date.now() - new Date(t.statusAlteradoEm).getTime()), 0);
    return soma / lista.length;
  }, [data]);

  const atendentesOnline = useMemo(
    () => (data?.atendentes || []).filter((a) => a.status === 'online').length,
    [data]
  );

  const atendentesOrdenados = useMemo(
    () => [...(data?.atendentes || [])].sort((a, b) => b.ticketsEmAtendimento - a.ticketsEmAtendimento),
    [data]
  );

  const totalAtendente = (a: AtendenteCarga) => a.pendentesAtribuidos + a.ticketsEmAtendimento + a.resolvidosPeriodo;
  const desempenhoOrdenado = useMemo(
    () => [...(data?.atendentes || [])].sort((a, b) => totalAtendente(b) - totalAtendente(a)),
    [data]
  );

  const abrirTicket = (ticket: PainelTicket, bucket: Bucket) => {
    setTicketSelecionado(ticket);
    setBucketSelecionado(bucket);
  };
  const fecharDrawer = () => {
    setTicketSelecionado(null);
    setBucketSelecionado(null);
  };

  const abrirConversa = (ticket: PainelTicket) => {
    navigate(`/dashboard?ticketId=${ticket.id}`);
  };

  if (!isGestor) return null;

  return (
    <div className="flex flex-col h-[100dvh] bg-gray-100 dark:bg-slate-900 overflow-hidden">
      <Header titulo="Painel de Operações" user={user} />

      <div className="flex-1 overflow-y-auto p-4 md:p-6 flex flex-col gap-4">
        <div>
          <Button variant="ghost" size="sm" className="gap-1.5 -ml-2" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" />
            Voltar para atendimentos
          </Button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Período (afeta só as métricas históricas abaixo — o quadro ao vivo nunca muda) */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-1">
                {(Object.keys(PERIODO_LABEL) as PeriodoPainel[]).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriodo(p)}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                      periodo === p
                        ? 'bg-sky-600 text-white'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    {PERIODO_LABEL[p]}
                  </button>
                ))}
              </div>
              <span className="text-[11px] text-gray-400">Métricas de desempenho no período selecionado — o quadro de tickets abaixo é sempre ao vivo</span>
            </div>

            {/* KPIs ao vivo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard label="Pendentes" value={pendentes.length} tone="warn" sub="ao vivo" live />
              <KpiCard label="Em Atendimento" value={atendimento.length} tone="accent" sub="ao vivo" live />
              <KpiCard
                label="Espera Média"
                value={esperaMediaMs !== null ? fmtElapsed(esperaMediaMs) : '—'}
                tone="default"
                sub="ao vivo"
                live
              />
              <KpiCard label="Atendentes Online" value={`${atendentesOnline}/${data?.atendentes.length ?? 0}`} tone="ok" sub="ao vivo" live />
            </div>

            {/* KPIs de período */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard label="Total de Atendimentos" value={data?.totalAtendimentosPeriodo ?? 0} tone="default" sub={PERIODO_LABEL[periodo]} />
              <KpiCard label="Demanda Ativa" value={data?.demandaAtivaPeriodo ?? 0} tone="accent" sub="empresa iniciou" />
              <KpiCard label="Demanda Receptiva" value={data?.demandaReceptivaPeriodo ?? 0} tone="accent" sub="cliente iniciou" />
              <KpiCard label="Novos Contatos" value={data?.novosContatosPeriodo ?? 0} tone="ok" sub={PERIODO_LABEL[periodo]} />
              <KpiCard label="TME" value={fmtDuracaoSegundos(data?.tmeSegundos ?? null)} tone="default" sub={PERIODO_LABEL[periodo]} />
              <KpiCard label="TMA" value={fmtDuracaoSegundos(data?.tmaSegundos ?? null)} tone="default" sub={PERIODO_LABEL[periodo]} />
              <KpiCard label="Resolvidos" value={data?.resolvidosPeriodo ?? 0} tone="default" sub={PERIODO_LABEL[periodo]} />
              <KpiCard label="Avaliação Média" value={fmtAvaliacao(data?.avaliacaoMediaPeriodo ?? null)} tone="ok" sub={PERIODO_LABEL[periodo]} />
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2">
              <div className="relative flex-1 min-w-[220px] max-w-sm">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por cliente, telefone ou protocolo…"
                  className="pl-8 h-8 text-sm"
                />
              </div>
              {filtroAtendenteId && (
                <Badge variant="secondary" className="cursor-pointer" onClick={() => setFiltroAtendenteId(null)}>
                  Filtrando por atendente ✕
                </Badge>
              )}
              <div className="flex items-center gap-3 ml-auto text-[11px] text-gray-400">
                <LegendaCor cor="bg-emerald-500" label="Normal" />
                <LegendaCor cor="bg-amber-500" label="Atenção" />
                <LegendaCor cor="bg-red-500" label="Parado há muito tempo" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  title="Atualizar"
                  onClick={() => fetchPainel(true)}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Board */}
            <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1.3fr_1fr] gap-4 items-start">
              <ColunaTickets
                titulo="Pendentes"
                subtitulo="Sem técnico atribuído · mais tempo parado no topo"
                tickets={pendentes}
                bucket="pendente"
                thresh={THRESH_PENDENTE}
                onSelect={abrirTicket}
              />
              <ColunaTickets
                titulo="Em Atendimento"
                subtitulo="Com técnico atribuído · mais tempo em atendimento no topo"
                tickets={atendimento}
                bucket="atendimento"
                thresh={THRESH_ATENDIMENTO}
                onSelect={abrirTicket}
              />

              {/* Grid de atendentes */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-bold text-sm">Atendentes</h2>
                    <Badge variant="secondary" className="text-[10px]">{atendentesOnline}/{atendentesOrdenados.length}</Badge>
                  </div>
                  <p className="text-[11px] text-gray-400 mb-3">Clique para filtrar o painel · chats ao vivo, resto no período ({PERIODO_LABEL[periodo].toLowerCase()})</p>
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 text-[10px] uppercase tracking-wide text-gray-400 px-2 pb-1.5">
                    <span>Atendente</span>
                    <span className="text-right w-8">Chats</span>
                    <span className="text-right w-14">Resolv.</span>
                    <span className="text-right w-10">Nota</span>
                  </div>
                  <div className="flex flex-col gap-1.5 max-h-[560px] overflow-y-auto">
                    {atendentesOrdenados.map((a) => (
                      <LinhaAtendente
                        key={a.id}
                        atendente={a}
                        selecionado={filtroAtendenteId === a.id}
                        onClick={() => setFiltroAtendenteId((prev) => (prev === a.id ? null : a.id))}
                      />
                    ))}
                    {atendentesOrdenados.length === 0 && (
                      <div className="text-center text-xs text-gray-400 py-6">Nenhum atendente ativo.</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Desempenho por usuário — período selecionado */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="font-bold text-sm">Desempenho por Usuário</h2>
                  <Badge variant="secondary" className="text-[10px]">{PERIODO_LABEL[periodo]}</Badge>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuário</TableHead>
                      <TableHead className="text-right">Pendentes</TableHead>
                      <TableHead className="text-right">Atendendo</TableHead>
                      <TableHead className="text-right">Resolvidos</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Avaliação</TableHead>
                      <TableHead className="text-right">TME</TableHead>
                      <TableHead className="text-right">TMA</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {desempenhoOrdenado.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>
                          <div className="flex items-center gap-2 min-w-0">
                            <Avatar className="h-7 w-7 flex-none">
                              <AvatarImage src={a.avatarUrl ? getFileUrl(a.avatarUrl) : undefined} />
                              <AvatarFallback className="text-[10px]">{getInitials(a.nome)}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <div className="text-xs font-semibold truncate">{a.nome}</div>
                              <div className="text-[10px] text-gray-400 truncate">{a.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="inline-flex justify-center min-w-[1.75rem] rounded-md bg-amber-400 text-white text-xs font-bold px-1.5 py-0.5">{a.pendentesAtribuidos}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="inline-flex justify-center min-w-[1.75rem] rounded-md bg-sky-500 text-white text-xs font-bold px-1.5 py-0.5">{a.ticketsEmAtendimento}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="inline-flex justify-center min-w-[1.75rem] rounded-md bg-emerald-500 text-white text-xs font-bold px-1.5 py-0.5">{a.resolvidosPeriodo}</span>
                        </TableCell>
                        <TableCell className="text-right text-sm font-semibold tabular-nums">{totalAtendente(a)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">
                          <span className="inline-flex items-center gap-1 justify-end">
                            {a.avaliacaoMedia !== null && <Star className="h-3 w-3 fill-amber-400 text-amber-400" />}
                            {fmtAvaliacao(a.avaliacaoMedia)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-gray-600 dark:text-gray-300">{fmtDuracaoSegundos(a.tmeSegundos)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-gray-600 dark:text-gray-300">{fmtDuracaoSegundos(a.tmaSegundos)}</TableCell>
                      </TableRow>
                    ))}
                    {desempenhoOrdenado.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-xs text-gray-400 py-6">Nenhum atendente ativo.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <Sheet open={!!ticketSelecionado} onOpenChange={(open) => !open && fecharDrawer()}>
        <SheetContent className="w-[380px] sm:max-w-[380px] overflow-y-auto">
          {ticketSelecionado && bucketSelecionado && (
            <TicketDrawer
              ticket={ticketSelecionado}
              bucket={bucketSelecionado}
              thresh={bucketSelecionado === 'pendente' ? THRESH_PENDENTE : THRESH_ATENDIMENTO}
              onAbrirConversa={abrirConversa}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const KpiCard: React.FC<{ label: string; value: React.ReactNode; tone: 'default' | 'warn' | 'accent' | 'ok'; sub: string; live?: boolean }> = ({ label, value, tone, sub, live }) => {
  const toneClass = {
    default: 'text-gray-900 dark:text-gray-100',
    warn: 'text-amber-600 dark:text-amber-400',
    accent: 'text-sky-600 dark:text-sky-400',
    ok: 'text-emerald-600 dark:text-emerald-400'
  }[tone];
  return (
    <Card>
      <CardContent className="p-3.5">
        <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-semibold mb-1">{label}</div>
        <div className={`text-2xl font-bold tabular-nums ${toneClass}`}>{value}</div>
        <div className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1">
          {live && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />}
          {sub}
        </div>
      </CardContent>
    </Card>
  );
};

const LegendaCor: React.FC<{ cor: string; label: string }> = ({ cor, label }) => (
  <div className="flex items-center gap-1.5">
    <span className={`h-2 w-2 rounded-full ${cor}`} />
    <span>{label}</span>
  </div>
);

const ColunaTickets: React.FC<{
  titulo: string;
  subtitulo: string;
  tickets: PainelTicket[];
  bucket: Bucket;
  thresh: { warn: number; crit: number };
  onSelect: (ticket: PainelTicket, bucket: Bucket) => void;
}> = ({ titulo, subtitulo, tickets, bucket, thresh, onSelect }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-1">
        <h2 className="font-bold text-sm">{titulo}</h2>
        <Badge variant="secondary" className="text-[10px]">{tickets.length}</Badge>
      </div>
      <p className="text-[11px] text-gray-400 mb-3">{subtitulo}</p>
      <div className="flex flex-col gap-2 max-h-[560px] overflow-y-auto">
        {tickets.map((t) => (
          <TicketCard key={t.id} ticket={t} thresh={thresh} onClick={() => onSelect(t, bucket)} />
        ))}
        {tickets.length === 0 && (
          <div className="text-center text-xs text-gray-400 border border-dashed border-gray-200 dark:border-slate-700 rounded-lg py-6">
            Nenhum ticket por aqui.
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const CanalIcon: React.FC<{ canal: CanalTicket }> = ({ canal }) => {
  if (canal === 'META') return <Mail className="h-3 w-3" />;
  if (canal === 'DESCONHECIDO') return <Phone className="h-3 w-3" />;
  return <MessageCircle className="h-3 w-3" />;
};

const TicketCard: React.FC<{ ticket: PainelTicket; thresh: { warn: number; crit: number }; onClick: () => void }> = ({ ticket, thresh, onClick }) => {
  const elapsed = Date.now() - new Date(ticket.statusAlteradoEm).getTime();
  const urg = urgencia(elapsed, thresh);
  return (
    <button
      onClick={onClick}
      className={`text-left w-full bg-gray-50 dark:bg-slate-700/40 hover:bg-gray-100 dark:hover:bg-slate-700 border-l-4 ${URGENCY_CLASSES[urg].split(' ')[0]} border border-gray-200 dark:border-slate-700 rounded-md px-3 py-2 transition-colors`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold text-sm truncate">{ticket.contato?.nome || ticket.contato?.telefone || 'Sem nome'}</span>
        <span className={`text-xs font-semibold tabular-nums flex items-center gap-1 ${URGENCY_CLASSES[urg].split(' ').slice(1).join(' ')}`}>
          {urg === 'crit' && <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />}
          {fmtElapsed(elapsed)}
        </span>
      </div>
      {ticket.assunto && <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{ticket.assunto}</div>}
      <div className="flex items-center gap-2 mt-2">
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded">
          <CanalIcon canal={ticket.canal} />
          {CANAL_LABEL[ticket.canal]}
        </span>
        {ticket.atendente && (
          <span className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
            <Avatar className="h-4 w-4">
              <AvatarImage src={ticket.atendente.avatarUrl ? getFileUrl(ticket.atendente.avatarUrl) : undefined} />
              <AvatarFallback className="text-[8px]">{getInitials(ticket.atendente.nome)}</AvatarFallback>
            </Avatar>
            {ticket.atendente.nome.split(' ')[0]}
          </span>
        )}
      </div>
    </button>
  );
};

const LinhaAtendente: React.FC<{ atendente: AtendenteCarga; selecionado: boolean; onClick: () => void }> = ({ atendente, selecionado, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-2 rounded-md px-2 py-1.5 border text-left transition-colors ${
        selecionado
          ? 'border-sky-400 bg-sky-50 dark:bg-sky-500/10'
          : 'border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/40 hover:bg-gray-100 dark:hover:bg-slate-700'
      }`}
    >
      <span className="flex items-center gap-2 min-w-0">
        <span className="relative flex-none">
          <Avatar className="h-6 w-6">
            <AvatarImage src={atendente.avatarUrl ? getFileUrl(atendente.avatarUrl) : undefined} />
            <AvatarFallback className="text-[9px]">{getInitials(atendente.nome)}</AvatarFallback>
          </Avatar>
          <span className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-white dark:border-slate-800 ${STATUS_ATENDENTE_DOT[atendente.status || 'offline']}`} />
        </span>
        <span className="min-w-0">
          <span className="block text-xs font-semibold truncate">{atendente.nome}</span>
          <span className="block text-[10px] text-gray-400">{STATUS_ATENDENTE_LABEL[atendente.status || 'offline']}</span>
        </span>
      </span>
      <span className="text-sm font-bold tabular-nums w-8 text-right">{atendente.ticketsEmAtendimento}</span>
      <span className="text-sm tabular-nums w-14 text-right text-gray-600 dark:text-gray-300">{atendente.resolvidosPeriodo}</span>
      <span className="text-xs tabular-nums w-10 text-right flex items-center justify-end gap-0.5 text-gray-600 dark:text-gray-300">
        {atendente.avaliacaoMedia !== null && <Star className="h-3 w-3 fill-amber-400 text-amber-400" />}
        {fmtAvaliacao(atendente.avaliacaoMedia)}
      </span>
    </button>
  );
};

const TicketDrawer: React.FC<{
  ticket: PainelTicket;
  bucket: Bucket;
  thresh: { warn: number; crit: number };
  onAbrirConversa: (ticket: PainelTicket) => void;
}> = ({ ticket, bucket, thresh, onAbrirConversa }) => {
  const elapsed = Date.now() - new Date(ticket.statusAlteradoEm).getTime();
  const urg = urgencia(elapsed, thresh);
  const criado = new Date(ticket.dataCriacao).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const ultimaAtualizacao = new Date(ticket.statusAlteradoEm).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="flex flex-col gap-5 mt-2">
      <SheetHeader>
        <SheetDescription className="text-xs tabular-nums">{ticket.protocolo}</SheetDescription>
        <SheetTitle>{ticket.contato?.nome || ticket.contato?.telefone}</SheetTitle>
      </SheetHeader>

      <div>
        <Badge variant={bucket === 'pendente' ? 'warning' : 'default'} className="uppercase text-[10px]">
          {bucket === 'pendente' ? 'Pendente' : 'Em atendimento'}
        </Badge>
        <div className={`text-3xl font-bold tabular-nums mt-2 ${URGENCY_CLASSES[urg].split(' ').slice(1).join(' ')}`}>
          {fmtElapsed(elapsed)}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">
          {bucket === 'pendente' ? 'tempo aguardando atribuição' : 'tempo em atendimento'}
        </div>
      </div>

      <Button variant="outline" className="justify-center gap-2" onClick={() => onAbrirConversa(ticket)}>
        <ExternalLink className="h-4 w-4" />
        Abrir conversa
      </Button>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Atendendo</div>
          {ticket.atendente ? (
            <div className="flex items-center gap-1.5">
              <Avatar className="h-5 w-5">
                <AvatarImage src={ticket.atendente.avatarUrl ? getFileUrl(ticket.atendente.avatarUrl) : undefined} />
                <AvatarFallback className="text-[9px]">{getInitials(ticket.atendente.nome)}</AvatarFallback>
              </Avatar>
              <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{ticket.atendente.nome}</span>
            </div>
          ) : (
            <p className="text-sm text-gray-400">Sem atendente</p>
          )}
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Fila</div>
          <p className="text-sm text-gray-600 dark:text-gray-300">{ticket.fila?.nome || 'Sem fila'}</p>
        </div>
        <div className="col-span-2">
          <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Última atualização</div>
          <p className="text-sm text-gray-600 dark:text-gray-300">{ultimaAtualizacao}</p>
        </div>
      </div>

      {ticket.assunto && (
        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Assunto</div>
          <p className="text-sm text-gray-600 dark:text-gray-300">{ticket.assunto}</p>
        </div>
      )}

      <div>
        <div className="text-[10.5px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Ticket criado</div>
        <p className="text-sm text-gray-600 dark:text-gray-300">{criado} · {CANAL_LABEL[ticket.canal]}</p>
      </div>

    </div>
  );
};

export default PainelOperacoesPage;
